import React from 'react';
import { useDraft } from '../../draft/DraftContext';
import { Box } from '@mui/material';
import CoverageGroupHeader from './CoverageGroupHeader';
import CoverageLineItem from './line-items/CoverageLineItem';
import CoverageSubgroupTitle from './CoverageSubgroupTitle';
import { isStandardCoverage } from './isStandardCoverage';
import { Show } from '../../../../../../../../components/Show';
import type { RaterXPreset } from '../../../../api/types/rater-x-preset';
import type { CoverageForms } from '../../../../api/types';
import SectionCard from '../../shared/SectionCard';
import SectionRatingDetails from '../../shared/SectionRatingDetails';
import SectionHeaderLabel from '../../shared/SectionHeaderLabel';
import ReferralStrategyDropdown from '../../shared/ReferralStrategyDropdown/ReferralStrategyDropdown';
import {
  ReferralStrategyVariant,
  getUnderwriteQuoteCurrency,
  isCoverageEndorsementsState,
} from '../../types';
import { shouldShowCoverage } from '../../../../utils/coverage-visibility';
import { useUnderwriterContext } from '../../provider/UnderwriterProvider';

const CoverageGroup = ({
  groups,
  title,
  categoryKey,
  ...props
}: CoverageGroupProps) => {
  // Subtotals for the staged draft, if one has priced. Undefined with the flag
  // off, so `SectionRatingDetails` renders committed exactly as it does today.
  const { rate } = useDraft();
  const draftCategory =
    rate.status === 'ok' ? rate.premiumByCategory[categoryKey] : undefined;
  const hasStandardCoverage = React.useMemo(() => {
    return groups.some((group) =>
      group.coverages
        .filter((coverage) => shouldShowCoverage(coverage, 'UW'))
        .some(
          (coverage) =>
            isStandardCoverage(coverage) ||
            (coverage.children ?? [])
              .filter((child) => shouldShowCoverage(child, 'UW'))
              .some((child) => isStandardCoverage(child))
        )
    );
  }, [groups]);

  const allCoverageKeys = React.useMemo(() => {
    return groups.flatMap((group) =>
      group.coverages
        .filter((coverage) => shouldShowCoverage(coverage, 'UW'))
        .map((coverage) => coverage.key)
    );
  }, [groups]);
  const context = useUnderwriterContext();
  const isMTEFlow = isCoverageEndorsementsState(context.state);

  return (
    <SectionCard>
      <CoverageGroupHeader
        title={title}
        showStandardColumns={hasStandardCoverage}
      >
        <Box display="flex" alignItems="center" gap="0.5rem">
          <SectionHeaderLabel
            title={title}
            appliedRules={props.appliedRules}
            warnings={props.warnings}
            referralReasons={props.referralReasons}
          />
          <Show when={!isMTEFlow}>
            {allCoverageKeys && allCoverageKeys.length > 0 && (
              <ReferralStrategyDropdown
                variant={ReferralStrategyVariant.CATEGORY_COVERAGE}
                keys={allCoverageKeys}
              />
            )}
          </Show>
        </Box>
      </CoverageGroupHeader>

      {groups.map((group) => {
        return (
          <React.Fragment key={group.title}>
            <Show when={groups.length > 1}>
              <CoverageSubgroupTitle>{group.title}</CoverageSubgroupTitle>
            </Show>

            {group.coverages
              .filter((coverage) => shouldShowCoverage(coverage, 'UW'))
              .map((coverage) => {
                return (
                  <React.Fragment key={coverage.id}>
                    <CoverageLineItem
                      id={coverage.key}
                      coverageForm={
                        props.coverageForms?.[coverage.field?.key ?? '']
                      }
                      isCoverageFormsLoading={props.isCoverageFormsLoading}
                    />
                    {coverage.children
                      ?.filter((child) => shouldShowCoverage(child, 'UW'))
                      .map((child) => (
                        <CoverageLineItem
                          id={child.key}
                          key={child.id}
                          isSubCoverage
                        />
                      ))}
                  </React.Fragment>
                );
              })}
          </React.Fragment>
        );
      })}
      <SectionRatingDetails
        ratingDetails={props.ratingDetails}
        draftPremium={draftCategory?.premium}
        draftAggregatePremium={draftCategory?.aggregatePremium}
        currency={
          isCoverageEndorsementsState(context.state)
            ? context.state.policy.currency
            : getUnderwriteQuoteCurrency(context.state)
        }
      />
    </SectionCard>
  );
};

export default CoverageGroup;

/**
 * `key` is reserved by React and never reaches props, so the category's own key
 * is threaded in under its own name for the draft-premium lookup.
 */
type CoverageGroupProps = RaterXPreset['coverageCategories'][0] & {
  readonly categoryKey: string;
  /**
   * Forms for the amendatory endorsements in this listing, fetched once by
   * `UnderwriterCoverageListing`. Absent for every non-amendatory category (FIR-21152).
   */
  readonly coverageForms?: CoverageForms;
  readonly isCoverageFormsLoading?: boolean;
};
